'use client';
import React from 'react';
import { useEffect, useState } from "react";
import { useParams } from 'react-router-dom';

import {
  About,
  ProfileHeader,
  UserProfileSidebar,
} from 'themes/jumbo/app/_components/user/profile';

import { ContentLayout } from 'themes/jumbo/app/_layout/ContentLayout';
import { getAssetPath } from 'themes/jumbo/app/_utilities/helpers';
import { useJumboTheme } from 'themes/jumbo/@jumbo/components/JumboTheme/hooks';
import { Stack, alpha, Checkbox, CircularProgress, Button, DialogProps, Typography } from '@mui/material';
import profileImg from "../../src/assets/images/profile-bg.jpg";
import AuthService from "services/AuthService";
import EventsEmitter from "events/EventsEmitter";
import { getCachedData } from "api/Cache";
import { CircularProgressWithLabel } from "components";
import { useDispatch, useSelector } from 'react-redux';
import { updateActiveUser, updateMaxUser, updateTeam } from 'store/main/userManagement';
import { RootState } from 'store';
import jwtService from 'services/jwtService';
import {CommonDialogBox} from "components";
import { closeDialog } from "store/main/newsLetterDialog";
import NotificationModel from 'components/notificationPanel/model/NotificationModel';
import { addNotification } from 'components/notificationPanel/store/dataSlice';
import { subscribeNewsletter } from "store/auth";

type IProject = {
    name: string;
    id : string;
};

const useProfileLayout = () => {
  	const { theme } = useJumboTheme();
  
	return React.useMemo(
		() => ({
		headerOptions: {
			sx: {
			mt: -4,
			mb: -7.25,
			mx: { xs: -4, lg: -6 },
			p: { xs: theme.spacing(6, 4, 11), lg: theme.spacing(6, 6, 11) },
			background: `#002447 url(${getAssetPath(`${profileImg}`, '1920x580')}) no-repeat center`,
			backgroundSize: 'cover',
			color: 'common.white',
			position: 'relative',

			'&::after': {
				display: 'inline-block',
				position: 'absolute',
				content: `''`,
				inset: 0,
				backgroundColor: alpha(theme.palette.common.black, 0.75),
			},
			},
		},
		sidebarOptions: {
			sx: {
			mr: 3.75,
			width: { xs: '100%', lg: '33%' },
			[theme.breakpoints.down('lg')]: {
				minHeight: 0,
				mr: 0,
				order: 2,
				mt: 3.75,
			},
			},
		},
		wrapperOptions: {
			sx: {
			[theme.breakpoints.down('lg')]: {
				flexDirection: 'column',
			},
			},
			container: true,
		},
		mainOptions: {
			sx: {
			[theme.breakpoints.down('lg')]: {
				minHeight: 0,
			},
			},
		},
		contentOptions: {
			sx: {
			p: { lg: 0, xs: 0 },
			},
		},
		}),
		[theme]
	);
};


const Profile = () => {
	const profileLayoutOptions = useProfileLayout();
	const data = getCachedData();
    const dialogBoxState = useSelector((state:RootState) => state.main.newsLetter.state)
    const {email, newsletter:newsletterState} = useSelector((state:RootState) => state.auth.data)
    const { defaultTab } = useParams();

    console.log("defaultTab : " + defaultTab);

    const dispatch = useDispatch();
	const active_user = useSelector((state:RootState) => state.main.addMember.active_user)
   	const max_user = useSelector((state:RootState) => state.main.addMember.max_user)
	const teams = useSelector((state:RootState) => state.main.addMember.team)
    const [plan, setPlan] = useState([])
    const [role, setRole] = useState('')
    const [progress, setProgress] = useState(0);
    const [allProjects, setAllProjects] = useState<IProject[]>([])

    const closeDialogBox: DialogProps["onClose"] = (event, reason) => {
        dispatch(closeDialog())
    }

    const displayErrorNotification = () => {
        dispatch(addNotification(NotificationModel({ message: 'We encountered an issue while sending your message. Please try again later or contact support.', options: { variant: 'error' } })));
    }

    const submitForm = async (option: string) => {
        const endpoint = process.env.REACT_APP_BASE_URL + '/newsletter/update';
        try{
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {
                    email: btoa(email),
                    option,
                    key_id : "e386cf6d8d4d2513c326a7bb0945b041"
                }
            });

            if(response.data) {
                if (response.data.error === false) {
                    dispatch(addNotification(NotificationModel({ message: 'Thank you! Your request has been sent successfully.', options: { variant: 'success' } })));
                    dispatch(closeDialog());
                    dispatch(subscribeNewsletter(option))
                } else {
                    displayErrorNotification();
                }
            } else{
                displayErrorNotification();
            }
            
        } catch(error){
            console.error(error);
            displayErrorNotification();
        }
    }

    const DialogAction = () => {
        return (
            <div style={{padding: '0 15px', display: 'flex', gap: '20px'}}>
               
                <Button 
                    type="button"
                    color="primary"
                    variant="outlined"
                    fullWidth
                    onClick={(evt:any) => submitForm('denied')}  
                >
                    Decline
                </Button>
                <Button 
                    type="submit" 
                    onClick={(evt:any) => submitForm('subscribed')}  
                    variant="contained"
                    color="primary"
                    fullWidth
                >
                    Subscribe
                </Button>
            </div>
        )
    }

    const NewsLetterDialogBox = () => {
        return (
            <CommonDialogBox
                classes={{
                    paper: 'm-24 rounded-8 white-bg'
                }}
                open={dialogBoxState && newsletterState.toUpperCase() === 'PENDING'}
                handleClose={closeDialogBox}
                fullWidth
                maxWidth="sm"
                subtitle1="Newsletter"
                titleh5=''
                dialogAction={<DialogAction />}
                displayScrollBar={true}
                maxHeight={200}
            >
                <label style={{display: 'flex', gap: '3px', alignItems: 'center', cursor: 'pointer'}}>
                    <Typography
                        fontSize={'16px'}
                        variant={'body1'}
                        color={'text.secondary'}
                    >
                        Join our newsletter for exclusive updates and offers.
                        <br />Click "Subscribe" to get the latest news and promotions from VoxelSpace.
                    </Typography>
                </label>               
            </CommonDialogBox>   
        );
    }

    const getPlan = async () => {
        try{
            const endpoint = process.env.REACT_APP_BASE_URL + '/subscription/email';
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {email : btoa(AuthService.userEmail), key_id : process.env.REACT_APP_BASE_APIKEY}
            });

            if(response.data.error === false){
                const result = response.data
          
                setPlan(result.data) 
              
                if(typeof result.data[0] !== "undefined"){
                dispatch(updateMaxUser(result.data[0]['ipn_qty']))
                dispatch(updateActiveUser(result.data[0]['active_users']))
                
                }
                setRole(result.role)      
            }
        }catch(err) {
            console.log(err);
        }
    }

    const getMyTeam = async () => {
        try{
            const endpoint = process.env.REACT_APP_BASE_URL + '/v2/customer/myusers';
            let response = await jwtService.fetchRequest({
                method: 'POST',
                url: endpoint,
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                },
                data: {email : btoa(AuthService.userEmail), key_id : process.env.REACT_APP_BASE_APIKEY}
            });

            if(response.data.error === false){
                dispatch(updateTeam(response.data.data))
            }
            
        }catch(err) {
            console.log(err);
        }
    }

    
    useEffect(() => {
        const handleProgress = (curProgress: number) => {
            setProgress(curProgress);
        };
        EventsEmitter.on("projects_progress", handleProgress);
        return () => {
            EventsEmitter.removeEventListener("projects_progress", handleProgress);
        };
      
    }, [data]);

    useEffect(() => {
        const projectsList = data["projects"];
        let projectTab: IProject[] = [];
        const keys = Object.keys(projectsList);
        keys.forEach((key, index)  => {
            projectTab.push({name:projectsList[key]['name'], id:projectsList[key]['ID']})
            if(index === keys.length - 1){
                setAllProjects(projectTab)
            }
        }) 
        getMyTeam()
        getPlan()
    }, [data]);

    
    if (!data.loaded) {
        return progress === 0 ? <CircularProgress /> : <CircularProgressWithLabel value={progress} />;
    }

    return (
        <>
            { plan.length > 0 ? (
                <ContentLayout
                    header={<ProfileHeader users={active_user} maxUsers={max_user}/>}
                    sidebar={<UserProfileSidebar />}
                    {...profileLayoutOptions}
                    >
                    <Stack spacing={3.75}>
                        <About plan={plan} team={teams} role={role} tab={defaultTab}/>
                        {/* <Biography />
                        <Events /> */}
                    </Stack>
                </ContentLayout>
            ) : (
                <ContentLayout
                    header={<ProfileHeader users={active_user} maxUsers={max_user}/>}
                    sidebar={<UserProfileSidebar />}
                    {...profileLayoutOptions}
                >
                    <Stack spacing={3.75}>
                        <About plan={plan} team={teams} tab={defaultTab}/>
                        {/* <Biography />
                        <Events /> */}
                    </Stack>
                </ContentLayout>
            )}
            <NewsLetterDialogBox />
        </>
    );
};

export default Profile;
